import { useCallback, useRef, useState } from 'react'
import { usePendingAttachments } from './usePendingAttachments'

/** True when a drag event carries files (vs. text/element drags). */
const isFilesDrag = (e: React.DragEvent) => !!e.dataTransfer?.types?.includes('Files')

/**
 * Photo attachment + drag-drop plumbing shared by the chat composers. Owns the
 * local-preview/upload queue (`usePendingAttachments`), the drag-overlay state,
 * the hidden file input, and the `attachmentIdsRef` the transport body reads for
 * the next outgoing message. `dragHandlers` spread straight onto the chat
 * container; when `enabled` is false they no-op so callers without attachments
 * stay drag-inert.
 */
export function useChatFileDrop(enabled = true) {
  const attachments = usePendingAttachments()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Ids of the photos riding the NEXT message — set right before send, read by
  // the (per-request) transport body, cleared once the request is sent.
  const attachmentIdsRef = useRef<string[]>([])
  const dragCounter = useRef(0)
  const [isDragging, setIsDragging] = useState(false)

  const { addFiles, pending, removeAt, clear, hasUploading, ready } = attachments
  const handleFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return
      addFiles(Array.from(files))
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [addFiles]
  )

  const dragHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!enabled || !isFilesDrag(e)) return
      e.preventDefault()
      dragCounter.current += 1
      if (dragCounter.current === 1) setIsDragging(true)
    },
    onDragOver: (e: React.DragEvent) => {
      if (!enabled || !isFilesDrag(e)) return
      e.preventDefault()
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!enabled || !isFilesDrag(e)) return
      dragCounter.current = Math.max(0, dragCounter.current - 1)
      if (dragCounter.current === 0) setIsDragging(false)
    },
    onDrop: (e: React.DragEvent) => {
      if (!enabled || !isFilesDrag(e)) return
      e.preventDefault()
      dragCounter.current = 0
      setIsDragging(false)
      handleFiles(e.dataTransfer.files)
    },
  }

  // Explicit surface — `addFiles` is intentionally not exported: callers go
  // through `handleFiles` (which also resets the file input), so exposing the
  // raw `addFiles` would be a footgun.
  return {
    pending,
    removeAt,
    clear,
    hasUploading,
    ready,
    fileInputRef,
    attachmentIdsRef,
    isDragging,
    dragHandlers,
    handleFiles,
  }
}
